import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import { scrollToSection } from "./Navbar";
import { API } from "@/lib/api";

const LOGO_IMG =
  "https://primary.jwwb.nl/public/o/r/n/temp-bkcxxqajnapgmncxuwyd/logo-high-0coj3f.jpg?enable-io=true&enable=upscale&fit=bounds&width=1200";

const LINES = ["310 METRES OF", "UNTAMED CLAY.", `${new Date().getFullYear() - 1968} YEARS OF DUST`, "& THUNDER."];
const NEXT_EVENT = new Date("2026-11-08T12:00:00+11:00");

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff / 3600000) % 24),
    m: Math.floor((diff / 60000) % 60),
    s: Math.floor((diff / 1000) % 60),
  };
}

const pad = (n) => String(n).padStart(2, "0");

export default function Hero({ onJoin }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [nextDate, setNextDate] = useState(NEXT_EVENT);
  const [nextLabel, setNextLabel] = useState("Season Opener");
  const [nextDisplay, setNextDisplay] = useState("08 Nov 2026");

  useEffect(() => {
    fetch(`${API}/events`)
      .then((r) => r.json())
      .then((evts) => {
        const now = new Date();
        const future = (Array.isArray(evts) ? evts : []).filter((e) => new Date(`${e.date}T23:59:59`) >= now);
        if (future.length) {
          const e = future[0];
          setNextDate(new Date(`${e.date}T12:00:00`));
          setNextLabel(e.title);
          setNextDisplay(
            new Date(`${e.date}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })
          );
        }
      })
      .catch(() => {});
  }, []);

  const { d, h, m, s } = useCountdown(nextDate);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <div className="absolute inset-0 grid place-items-center overflow-hidden">
          <img
            src={LOGO_IMG}
            alt=""
            aria-hidden
            draggable={false}
            className="w-[min(85vw,820px)] opacity-35 mix-blend-screen select-none"
          />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(11,10,9,0.05)_0%,rgba(11,10,9,0.9)_75%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-clay-bg via-transparent to-clay-bg/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] w-full mx-auto px-4 sm:px-8 pb-14 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          className="font-mono text-xs sm:text-sm uppercase tracking-[0.35em] text-clay-gold mb-6 flex items-center gap-3"
        >
          <MapPin className="w-4 h-4 text-clay-orange" />
          Mortlake Car Club — Darlington, Victoria
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display font-black uppercase leading-[0.9] tracking-tight text-5xl sm:text-7xl lg:text-8xl xl:text-9xl"
        >
          {LINES.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                className={`block ${i === 3 ? "text-clay-orange" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end gap-10 justify-between">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-join-cta"
              onClick={onJoin}
              className="bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 hover:-translate-y-1 clay-glow"
            >
              Become a Member — $35/Season
            </button>
            <button
              data-testid="hero-schedule-cta"
              onClick={() => scrollToSection("#schedule")}
              className="border border-clay-border hover:border-clay-orange text-clay-text font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 hover:-translate-y-1 bg-clay-bg/40 backdrop-blur-sm"
            >
              2026/27 Calendar
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="flex items-center gap-6 border border-clay-border/70 bg-clay-surface/60 backdrop-blur-md px-6 py-4"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted leading-relaxed max-w-[150px]">
              {nextLabel}
              <span className="block text-clay-gold">{nextDisplay}</span>
            </div>
            {[
              [d, "days"],
              [pad(h), "hrs"],
              [pad(m), "min"],
              [pad(s), "sec"],
            ].map(([v, label]) => (
              <div key={label} className="text-center">
                <div className="font-display font-black text-3xl sm:text-4xl text-clay-text tabular-nums">{v}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-clay-muted">{label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        aria-label="Scroll down"
        onClick={() => scrollToSection("#legacy")}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        className="absolute bottom-6 right-6 text-clay-muted hover:text-clay-gold transition-colors hidden lg:block"
      >
        <ChevronDown className="w-6 h-6" />
      </motion.button>
    </section>
  );
}
